import { useCallback, useRef, useState } from 'react';
import { parseJsonSafe } from '../api/client';
import { postEndSession, readActiveSessionConflict } from '../api/tutor';
import { getSessionRuntime, getSessionRuntimePath } from '../lib/sessionRouting';

/**
 * The single implementation of "the backend says this student is already in a
 * session".
 *
 * Every session-creation entry point (student dashboard, admin dashboard form,
 * My Students, practice) hits the same structured 409 and owes the learner the
 * same two choices: resume the session that exists, or end it and start the one
 * they asked for. Silently reusing and silently ending are both forbidden — the
 * session belongs to the learner (docs/01_product/session_lifecycle.md:96-99).
 * Sharing this hook is what stops those call sites from drifting apart.
 */

/**
 * Read a create failure into a conflict, or null. Only a 409 can be one; every
 * other status keeps the caller's own error handling.
 */
export function readActiveSessionConflictFromError(error) {
  if (Number(error?.status) !== 409) {
    return null;
  }
  return readActiveSessionConflict(error?.body);
}

/**
 * Turn a non-ok `Response` into an Error that carries the status and parsed body,
 * so a Response-based call site produces exactly the shape
 * `readActiveSessionConflictFromError` reads.
 */
export function createSessionRequestError(response, data, fallbackMessage) {
  const message =
    (typeof data?.detail === 'string' && data.detail) ||
    (typeof data?.detail?.message === 'string' && data.detail.message) ||
    (typeof data?.message === 'string' && data.message) ||
    fallbackMessage;
  const error = new Error(message);
  error.status = response?.status;
  error.detail = data?.detail;
  error.body = data;
  return error;
}

/**
 * @param {object} options
 * @param {(path: string) => void} options.navigate  react-router navigate
 * @param {(error: Error) => void} [options.onEndError]    ending the existing session failed
 * @param {(error: Error) => void} [options.onRetryError]  the retried create failed
 * @param {(sessionId: any) => Promise<Response>} [options.endSession] injection seam for tests
 */
export default function useActiveSessionConflict({
  navigate,
  onEndError,
  onRetryError,
  endSession = postEndSession,
} = {}) {
  const [conflict, setConflict] = useState(null);
  const [isResolving, setIsResolving] = useState(false);
  // The exact create the caller asked for, so "end it and start a new session"
  // retries the ORIGINAL payload rather than a re-derived approximation.
  const pendingCreateRef = useRef(null);

  const clearConflict = useCallback(() => {
    setConflict(null);
    pendingCreateRef.current = null;
  }, []);

  /**
   * Run a session create. Returns true when it went through, false when it was
   * answered with an ACTIVE_SESSION_EXISTS 409 (the prompt is now showing).
   * Any other failure is rethrown so the caller keeps its own error handling.
   */
  const guardCreate = useCallback(async (createFn) => {
    setConflict(null);
    pendingCreateRef.current = createFn;
    try {
      await createFn();
      pendingCreateRef.current = null;
      return true;
    } catch (error) {
      const detected = readActiveSessionConflictFromError(error);
      if (detected) {
        setConflict(detected);
        return false;
      }
      pendingCreateRef.current = null;
      throw error;
    }
  }, []);

  const resume = useCallback(() => {
    if (!conflict || isResolving) {
      return;
    }
    clearConflict();
    // The runtime is a property of the existing session's mode, never of the
    // page the learner happened to be on: an EXAM session must not open in the
    // tutor runtime.
    navigate(getSessionRuntimePath(conflict.sessionId, getSessionRuntime({ mode: conflict.mode })));
  }, [clearConflict, conflict, isResolving, navigate]);

  const endAndStartNew = useCallback(async () => {
    const pendingCreate = pendingCreateRef.current;
    if (!conflict || isResolving) {
      return;
    }

    setIsResolving(true);
    try {
      const response = await endSession(conflict.sessionId);
      if (!response?.ok) {
        const data = await parseJsonSafe(response);
        throw createSessionRequestError(response, data, 'Failed to end the existing session.');
      }
    } catch (error) {
      setIsResolving(false);
      if (onEndError) {
        onEndError(error);
      }
      return;
    }

    setIsResolving(false);
    setConflict(null);
    if (!pendingCreate) {
      return;
    }
    try {
      await guardCreate(pendingCreate);
    } catch (error) {
      if (onRetryError) {
        onRetryError(error);
      }
    }
  }, [conflict, endSession, guardCreate, isResolving, onEndError, onRetryError]);

  return {
    conflict,
    isResolving,
    clearConflict,
    guardCreate,
    resume,
    endAndStartNew,
  };
}
